<template>
  <div class="qa-wrapper" v-click-outside="closePanel">
    <!-- Trigger Button -->
    <v-btn
      x-small
      dark
      class="mr-2 text-capitalize nav-action-btn qa-trigger-btn"
      data-tour="qa-trigger"
      :style="{
        background: panelOpen
          ? 'linear-gradient(135deg, #F59E0B 0%, #EF4444 50%, #EC4899 100%)'
          : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
      }"
      @click.stop="togglePanel"
    >
      <v-icon x-small class="mr-1">{{ panelOpen ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
      Quick Access
    </v-btn>

    <!-- Dropdown Panel (rendered inline, not in detached overlay) -->
    <div v-if="panelOpen" class="qa-dropdown" @click.stop>
      <v-card class="qa-panel-card" elevation="8">
        <!-- Header -->
        <div class="qa-header">
          <div class="qa-header-left">
            <v-icon size="18" color="#6366F1" class="mr-2">mdi-star-circle</v-icon>
            <span class="qa-title">Quick Access</span>
          </div>
          <div class="qa-header-right">
            <span class="qa-count">{{ favouriteCount }}/10</span>
            <v-btn icon x-small class="ml-1 qa-help-btn" title="How it works" @click="startTour">
              <v-icon size="15" color="#6366F1">mdi-help-circle-outline</v-icon>
            </v-btn>
            <v-btn icon x-small class="ml-0" @click="closePanel">
              <v-icon size="16" color="#94A3B8">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Onboarding tip (dismissible) -->
        <div v-if="showTip" class="qa-tip">
          <div class="qa-tip-content">
            <div class="qa-tip-title">
              <v-icon size="14" color="#4F46E5" class="mr-1">mdi-information-outline</v-icon>
              How it works
            </div>
            <div class="qa-tip-steps">
              <div class="qa-tip-step">
                <v-icon size="12" color="#F59E0B">mdi-star</v-icon>
                <span>Tap the <b>star</b> next to any module to pin it</span>
              </div>
              <div class="qa-tip-step">
                <v-icon size="12" color="#6366F1">mdi-chevron-up</v-icon>
                <span>Use <b>arrows</b> to reorder your shortcuts</span>
              </div>
              <div class="qa-tip-step">
                <v-icon size="12" color="#10B981">mdi-circle-medium</v-icon>
                <span>Pinned modules appear as <b>icons in the top bar</b></span>
              </div>
            </div>
          </div>
          <v-btn icon x-small class="qa-tip-close" @click="dismissTip">
            <v-icon size="14" color="#94A3B8">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Search -->
        <div class="qa-search-wrap" data-tour="qa-search">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search modules..."
            dense
            hide-details
            outlined
            class="qa-search-input"
            prepend-inner-icon="mdi-magnify"
            clearable
          ></v-text-field>
        </div>

        <div class="qa-body">
          <!-- My Favourites Section -->
          <div v-if="!searchQuery" class="qa-section" data-tour="qa-shortcuts">
            <div class="qa-section-header">
              <span class="qa-section-title">My Shortcuts</span>
              <span class="qa-section-badge">{{ favouriteCount }}</span>
            </div>

            <div v-if="favouriteItems.length === 0" class="qa-empty">
              <v-icon size="28" color="#CBD5E1" class="mb-1">mdi-star-off-outline</v-icon>
              <div class="qa-empty-text">No shortcuts yet</div>
              <div class="qa-empty-sub">Star modules below to add them here</div>
            </div>

            <div v-else class="qa-fav-list">
              <div
                v-for="(fav, idx) in favouriteItems"
                :key="'fav-' + fav.module_key"
                class="qa-fav-item"
              >
                <div class="qa-fav-drag">
                  <v-icon size="14" color="#CBD5E1">mdi-drag-vertical</v-icon>
                </div>
                <div
                  class="qa-fav-icon"
                  :style="{ background: getItemIconColor(fav.module_key, fav.module_category) }"
                >
                  <v-icon v-if="hasMeaningfulIcon(fav.module_icon)" size="13" color="#fff">{{ fav.module_icon }}</v-icon>
                  <span v-else class="qa-avatar-text">{{ getInitials(fav.module_name) }}</span>
                </div>
                <div class="qa-fav-name" @click="navigateTo(fav.module_route)">
                  {{ fav.module_name }}
                </div>
                <div class="qa-fav-actions">
                  <v-btn icon x-small @click="moveUp(idx)" :disabled="idx === 0" class="qa-move-btn">
                    <v-icon size="14">mdi-chevron-up</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="moveDown(idx)" :disabled="idx === favouriteItems.length - 1" class="qa-move-btn">
                    <v-icon size="14">mdi-chevron-down</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="removeFav(fav.module_key)" class="qa-remove-btn">
                    <v-icon size="14" color="#EF4444">mdi-star</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <v-divider v-if="!searchQuery" class="qa-divider"></v-divider>

          <!-- All Modules Section -->
          <div class="qa-section" data-tour="qa-all-modules">
            <div class="qa-section-header">
              <span class="qa-section-title">{{ searchQuery ? 'Search Results' : 'All Modules' }}</span>
            </div>

            <div v-if="filteredGrouped.length === 0" class="qa-empty">
              <v-icon size="28" color="#CBD5E1" class="mb-1">mdi-magnify</v-icon>
              <div class="qa-empty-text">No modules found</div>
            </div>

            <div v-for="group in filteredGrouped" :key="group.category" class="qa-category-group">
              <div class="qa-category-header">
                <div
                  class="qa-category-dot"
                  :style="{ background: getCategoryColor(group.category) }"
                ></div>
                <span class="qa-category-name">{{ group.category }}</span>
                <span class="qa-category-count">{{ group.items.length }}</span>
              </div>

              <div
                v-for="item in group.items"
                :key="'mod-' + item.key"
                class="qa-module-item"
              >
                <div
                  class="qa-module-icon"
                  :style="{ background: getItemIconColor(item.key, item.category) }"
                >
                  <v-icon v-if="hasMeaningfulIcon(item.icon)" size="13" color="#fff">{{ item.icon }}</v-icon>
                  <span v-else class="qa-avatar-text">{{ getInitials(item.name) }}</span>
                </div>
                <div class="qa-module-name" @click="navigateTo(item.route)">
                  {{ item.name }}
                </div>
                <v-btn
                  icon
                  small
                  @click="toggleFav(item)"
                  class="qa-star-btn"
                  :data-tour="'qa-star-' + item.key"
                  :disabled="!isFav(item.key) && favouriteCount >= 10"
                >
                  <v-icon size="22" :color="isFav(item.key) ? '#F59E0B' : '#CBD5E1'">
                    {{ isFav(item.key) ? 'mdi-star' : 'mdi-star-outline' }}
                  </v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Spotlight Tour Overlay -->
    <template v-if="tourActive">
      <svg class="qa-tour-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="qa-tour-mask">
            <rect width="100%" height="100%" fill="white"/>
            <rect v-if="tourCurrentStep.target && tourSpot.width > 0"
              :x="tourSpot.left" :y="tourSpot.top"
              :width="tourSpot.width" :height="tourSpot.height"
              rx="10" fill="black"/>
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(18,18,38,.82)" mask="url(#qa-tour-mask)"/>
      </svg>
      <div v-if="tourCurrentStep.target && tourSpot.width > 0" class="qa-tour-ring"
        :style="{ top: tourSpot.top + 'px', left: tourSpot.left + 'px', width: tourSpot.width + 'px', height: tourSpot.height + 'px' }"></div>
      <div class="qa-tour-card" :style="tourCardStyle">
        <div class="qa-tour-progress">
          <div v-for="(s, i) in tourSteps" :key="'ts-' + i"
            class="qa-tour-dot" :class="{ active: i === tourStep }"></div>
        </div>
        <div class="qa-tour-step-num">Step {{ tourStep + 1 }} of {{ tourSteps.length }}</div>
        <div class="qa-tour-title">{{ tourCurrentStep.title }}</div>
        <div class="qa-tour-body">{{ tourCurrentStep.body }}</div>
        <div class="qa-tour-actions">
          <button class="qa-tour-skip" @click="endTour">Skip</button>
          <div class="qa-tour-nav">
            <button v-if="tourStep > 0" class="qa-tour-btn qa-tour-btn-outline" @click="prevTourStep">&#8592; Back</button>
            <button class="qa-tour-btn qa-tour-btn-primary" @click="nextTourStep">
              {{ tourStep === tourSteps.length - 1 ? 'Got it!' : 'Next &#8594;' }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
var QuickAccessItems = require("@/components/Menu/QuickAccessItems");
var ALL_ITEMS = QuickAccessItems.ALL_QUICK_ACCESS_ITEMS;
var CATEGORY_GRADIENTS = QuickAccessItems.CATEGORY_GRADIENTS;
var CATEGORY_COLORS = QuickAccessItems.CATEGORY_COLORS;
var ITEM_ICON_COLORS = QuickAccessItems.ITEM_ICON_COLORS;

export default {
  name: "QuickAccessPanel",
  data: function () {
    var tipDismissed = false;
    try { tipDismissed = localStorage.getItem("qa_tip_dismissed") === "1"; } catch (e) { /* silent */ }
    return {
      panelOpen: false,
      searchQuery: "",
      showTip: !tipDismissed,
      tourActive: false,
      tourStep: 0,
      tourSpot: { top: 0, left: 0, width: 0, height: 0 },
    };
  },
  computed: {
    favouriteItems: function () {
      return this.$store.getters["quickAccessFavourites/favouritesList"];
    },
    favouriteKeys: function () {
      return this.$store.getters["quickAccessFavourites/favouriteKeys"];
    },
    favouriteCount: function () {
      return this.favouriteItems.length;
    },
    accessibleItems: function () {
      // Filter ALL_ITEMS by user's screen rights
      var screenRights = [];
      try {
        var raw = localStorage.getItem("SCREEN_RIGHT_DATASET");
        if (raw) screenRights = JSON.parse(raw);
      } catch (e) {
        screenRights = [];
      }
      var screenCodes = {};
      for (var i = 0; i < screenRights.length; i++) {
        if (screenRights[i].screen_code) {
          screenCodes[screenRights[i].screen_code] = true;
        }
      }

      var moduleRights = [];
      try {
        var rawM = localStorage.getItem("MODULE_RIGHTS_DATASET");
        if (rawM) moduleRights = JSON.parse(rawM);
      } catch (e) {
        moduleRights = [];
      }
      var moduleCodes = {};
      for (var j = 0; j < moduleRights.length; j++) {
        if (moduleRights[j].module_code) {
          moduleCodes[moduleRights[j].module_code] = true;
        }
      }

      var result = [];
      for (var k = 0; k < ALL_ITEMS.length; k++) {
        var item = ALL_ITEMS[k];
        // Items with no screen_codeflag or module_codeflag are always accessible
        var hasScreenFlag = item.screen_codeflag && item.screen_codeflag.length > 0;
        var hasModuleFlag = item.module_codeflag && item.module_codeflag.length > 0;

        if (!hasScreenFlag && !hasModuleFlag) {
          result.push(item);
          continue;
        }

        // Check screen flags
        var screenOk = true;
        if (hasScreenFlag) {
          screenOk = false;
          for (var s = 0; s < item.screen_codeflag.length; s++) {
            if (screenCodes[item.screen_codeflag[s]]) {
              screenOk = true;
              break;
            }
          }
        }

        // Check module flags
        var moduleOk = true;
        if (hasModuleFlag) {
          moduleOk = false;
          for (var mm = 0; mm < item.module_codeflag.length; mm++) {
            if (moduleCodes[item.module_codeflag[mm]]) {
              moduleOk = true;
              break;
            }
          }
        }

        if (screenOk && moduleOk) {
          result.push(item);
        }
      }
      return result;
    },
    filteredGrouped: function () {
      var items = this.accessibleItems;
      var query = (this.searchQuery || "").toLowerCase().trim();

      // Filter by search
      if (query) {
        items = items.filter(function (item) {
          return (
            item.name.toLowerCase().indexOf(query) !== -1 ||
            item.category.toLowerCase().indexOf(query) !== -1 ||
            item.key.toLowerCase().indexOf(query) !== -1
          );
        });
      }

      // Group by category
      var groups = {};
      var categoryOrder = [];
      for (var i = 0; i < items.length; i++) {
        var cat = items[i].category || "Other";
        if (!groups[cat]) {
          groups[cat] = [];
          categoryOrder.push(cat);
        }
        groups[cat].push(items[i]);
      }

      var result = [];
      for (var c = 0; c < categoryOrder.length; c++) {
        result.push({
          category: categoryOrder[c],
          items: groups[categoryOrder[c]],
        });
      }
      return result;
    },
    tourSteps: function () {
      return [
        { target: null, title: "Welcome to Quick Access", body: "Quick Access lets you pin your most-used modules as shortcuts in the top bar. Let's walk through how it works.", placement: "center" },
        { target: "[data-tour='qa-trigger']", title: "Quick Access Button", body: "Click this button anytime to open the Quick Access panel and manage your shortcuts.", placement: "bottom" },
        { target: "[data-tour='qa-search']", title: "Search Modules", body: "Type here to quickly find any module by name or category.", placement: "bottom" },
        { target: "[data-tour='qa-shortcuts']", title: "My Shortcuts", body: "Your pinned modules appear here. Use the up/down arrows to reorder them. Click a name to navigate directly.", placement: "right" },
        { target: "[data-tour='qa-all-modules']", title: "All Modules", body: "Browse all available modules grouped by category. Tap the star icon next to any module to pin or unpin it.", placement: "top" },
        { target: ".qa-fav-avatar", title: "Top Bar Icons", body: "Your pinned shortcuts appear as icons in the top bar for one-click access. You can pin up to 10 modules.", placement: "bottom" },
      ];
    },
    tourCurrentStep: function () {
      return this.tourSteps[this.tourStep] || {};
    },
    tourCardStyle: function () {
      var s = this.tourCurrentStep;
      if (!s.target || s.placement === "center") {
        return { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "320px" };
      }
      var r = this.tourSpot;
      var W = 310, CH = 240, pad = 16;
      var vpW = window.innerWidth, vpH = window.innerHeight;
      var sl = function (l) { return Math.max(8, Math.min(l, vpW - W - 8)); };
      var st = function (t) { return Math.max(8, Math.min(t, vpH - CH - 8)); };
      if (s.placement === "bottom") {
        var wT = r.top + r.height + pad;
        return { position: "fixed", top: ((wT + CH > vpH - 8) ? Math.max(8, r.top - CH - pad) : wT) + "px", left: sl(r.left) + "px", width: W + "px" };
      }
      if (s.placement === "top") {
        return { position: "fixed", top: Math.max(8, r.top - CH - pad) + "px", left: sl(r.left) + "px", width: W + "px" };
      }
      if (s.placement === "right") {
        var wL = r.left + r.width + pad;
        return { position: "fixed", top: st(r.top) + "px", left: ((wL + W > vpW - 8) ? Math.max(8, r.left - W - pad) : wL) + "px", width: W + "px" };
      }
      // left
      var wL2 = r.left - W - pad;
      return { position: "fixed", top: st(r.top) + "px", left: Math.max(8, (wL2 < 8 ? r.left + r.width + pad : wL2)) + "px", width: W + "px" };
    },
  },
  methods: {
    togglePanel: function () {
      this.panelOpen = !this.panelOpen;
      if (!this.panelOpen) {
        this.searchQuery = "";
      }
    },
    dismissTip: function () {
      this.showTip = false;
      try { localStorage.setItem("qa_tip_dismissed", "1"); } catch (e) { /* silent */ }
    },
    startTour: function () {
      this.showTip = false;
      // Make sure panel is open so tour targets are visible
      if (!this.panelOpen) this.panelOpen = true;
      this.tourStep = 0;
      this.tourActive = true;
      var self = this;
      this.$nextTick(function () { self._updateTourSpot(); });
    },
    endTour: function () {
      this.tourActive = false;
    },
    nextTourStep: function () {
      if (this.tourStep < this.tourSteps.length - 1) {
        this.tourStep++;
        var self = this;
        this.$nextTick(function () { self._updateTourSpot(); });
      } else {
        this.endTour();
      }
    },
    prevTourStep: function () {
      if (this.tourStep > 0) {
        this.tourStep--;
        var self = this;
        this.$nextTick(function () { self._updateTourSpot(); });
      }
    },
    _updateTourSpot: function () {
      var s = this.tourSteps[this.tourStep];
      if (!s || !s.target) {
        this.tourSpot = { top: 0, left: 0, width: 0, height: 0 };
        return;
      }
      var el = document.querySelector(s.target);
      if (el) {
        var r = el.getBoundingClientRect();
        var p = 8;
        this.tourSpot = { top: r.top - p, left: r.left - p, width: r.width + p * 2, height: r.height + p * 2 };
      } else {
        this.tourSpot = { top: 0, left: 0, width: 0, height: 0 };
      }
    },
    closePanel: function () {
      if (this.panelOpen) {
        this.panelOpen = false;
        this.searchQuery = "";
      }
    },
    hasMeaningfulIcon: function (icon) {
      return icon && icon !== "" && icon !== "mdi-circle-outline";
    },
    getInitials: function (name) {
      if (!name) return "?";
      var words = name.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    isFav: function (key) {
      return this.$store.getters["quickAccessFavourites/isFavourite"](key);
    },
    toggleFav: function (item) {
      if (this.isFav(item.key)) {
        this.$store.dispatch("quickAccessFavourites/removeFavourite", item.key);
      } else {
        if (this.favouriteCount >= 10) return;
        this.$store.dispatch("quickAccessFavourites/addFavourite", item);
      }
    },
    removeFav: function (moduleKey) {
      this.$store.dispatch("quickAccessFavourites/removeFavourite", moduleKey);
    },
    moveUp: function (idx) {
      if (idx === 0) return;
      var keys = this.favouriteKeys.slice();
      var temp = keys[idx];
      keys[idx] = keys[idx - 1];
      keys[idx - 1] = temp;
      this.$store.dispatch("quickAccessFavourites/reorderFavourites", keys);
    },
    moveDown: function (idx) {
      if (idx >= this.favouriteItems.length - 1) return;
      var keys = this.favouriteKeys.slice();
      var temp = keys[idx];
      keys[idx] = keys[idx + 1];
      keys[idx + 1] = temp;
      this.$store.dispatch("quickAccessFavourites/reorderFavourites", keys);
    },
    navigateTo: function (route) {
      this.panelOpen = false;
      this.searchQuery = "";
      if (this.$route.path === route) return;
      this.$router.push(route).catch(function (err) {
        if (err && err.name !== "NavigationDuplicated") throw err;
      });
    },
    getCategoryGradient: function (category) {
      return CATEGORY_GRADIENTS[category] || "linear-gradient(90deg, #64748B 0%, #475569 50%, #334155 100%)";
    },
    getCategoryColor: function (category) {
      return CATEGORY_COLORS[category] || "#64748B";
    },
    getItemIconColor: function (key, category) {
      return ITEM_ICON_COLORS[key] || CATEGORY_COLORS[category] || "#94A3B8";
    },
  },
};
</script>

<style scoped>
/* ── Wrapper: position anchor for the dropdown ── */
.qa-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* ── Dropdown panel: positioned below the trigger button ── */
.qa-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  z-index: 10000;
  animation: qaSlideDown 0.18s ease-out;
}

@keyframes qaSlideDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.qa-panel-card {
  width: 400px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
}

/* Header */
.qa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #F1F5F9;
}

.qa-header-left {
  display: flex;
  align-items: center;
}

.qa-header-right {
  display: flex;
  align-items: center;
}

.qa-title {
  font-size: 14px;
  font-weight: 700;
  color: #1E293B;
  letter-spacing: -0.2px;
}

.qa-count {
  font-size: 11px;
  font-weight: 600;
  color: #6366F1;
  background: #EEF2FF;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Onboarding tip */
.qa-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 8px 12px 0;
  padding: 10px 12px;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  border-radius: 10px;
}

.qa-tip-content {
  flex: 1;
}

.qa-tip-title {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #4338CA;
  margin-bottom: 6px;
}

.qa-tip-steps {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qa-tip-step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  color: #475569;
  line-height: 1.3;
}

.qa-tip-step b {
  font-weight: 600;
  color: #1E293B;
}

.qa-tip-close {
  flex-shrink: 0;
  margin-top: -2px;
}

/* Search */
.qa-search-wrap {
  padding: 8px 12px;
}

.qa-search-input.v-text-field.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  min-height: 34px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
}

.qa-search-input >>> .v-input__prepend-inner {
  margin-top: 6px !important;
}

/* Body — scrollable */
.qa-body {
  overflow-y: auto;
  max-height: 420px;
  padding-bottom: 8px;
}

.qa-body::-webkit-scrollbar {
  width: 4px;
}

.qa-body::-webkit-scrollbar-track {
  background: transparent;
}

.qa-body::-webkit-scrollbar-thumb {
  background: #E2E8F0;
  border-radius: 2px;
}

/* Sections */
.qa-section {
  padding: 0 12px;
}

.qa-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 4px 6px;
}

.qa-section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94A3B8;
}

.qa-section-badge {
  font-size: 9px;
  font-weight: 600;
  color: #6366F1;
  background: #EEF2FF;
  padding: 1px 6px;
  border-radius: 8px;
}

/* Empty state */
.qa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.qa-empty-text {
  font-size: 12px;
  font-weight: 600;
  color: #94A3B8;
}

.qa-empty-sub {
  font-size: 11px;
  color: #CBD5E1;
  margin-top: 2px;
}

/* Favourite list items */
.qa-fav-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qa-fav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  border-radius: 8px;
  transition: background 0.15s;
}

.qa-fav-item:hover {
  background: #F8FAFC;
}

.qa-fav-drag {
  cursor: grab;
  opacity: 0.4;
  flex-shrink: 0;
}

.qa-fav-item:hover .qa-fav-drag {
  opacity: 0.8;
}

.qa-fav-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qa-fav-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qa-fav-name:hover {
  color: #6366F1;
}

.qa-fav-actions {
  display: flex;
  align-items: center;
  gap: 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.qa-fav-item:hover .qa-fav-actions {
  opacity: 1;
}

.qa-move-btn {
  width: 22px !important;
  height: 22px !important;
}

.qa-remove-btn {
  width: 22px !important;
  height: 22px !important;
}

.qa-divider {
  margin: 4px 12px !important;
}

/* Category groups */
.qa-category-group {
  margin-bottom: 4px;
}

.qa-category-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 4px 4px;
}

.qa-category-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.qa-category-name {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94A3B8;
}

.qa-category-count {
  font-size: 9px;
  color: #CBD5E1;
}

/* Module items (all modules list) */
.qa-module-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 8px;
  transition: background 0.15s;
}

.qa-module-item:hover {
  background: #F8FAFC;
}

.qa-module-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qa-avatar-text {
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.2px;
  line-height: 1;
  user-select: none;
}

.qa-module-name {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qa-module-name:hover {
  color: #6366F1;
}

.qa-star-btn {
  flex-shrink: 0;
}

/* Trigger button override */
.qa-trigger-btn {
  border-radius: 6px !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
  height: 26px !important;
  padding: 0 10px !important;
  font-size: 11px !important;
  background-color: transparent !important;
  transition: all 0.25s ease !important;
}

.qa-trigger-btn::before {
  opacity: 0 !important;
}

/* Help button in header */
.qa-help-btn {
  width: 22px !important;
  height: 22px !important;
}

/* ── Spotlight Tour ── */
.qa-tour-svg {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10100;
  pointer-events: none;
}

.qa-tour-ring {
  position: fixed;
  border-radius: 12px;
  border: 2px solid #6366F1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25);
  z-index: 10101;
  pointer-events: none;
  transition: all 0.3s;
}

.qa-tour-card {
  position: fixed;
  z-index: 10102;
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 12px 40px rgba(26, 26, 46, 0.2);
  border: 1px solid #EEECF5;
}

.qa-tour-progress {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.qa-tour-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #EEECF5;
  transition: all 0.2s;
}

.qa-tour-dot.active {
  background: #6366F1;
  width: 16px;
  border-radius: 3px;
}

.qa-tour-step-num {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #9A96A8;
  margin-bottom: 6px;
}

.qa-tour-title {
  font-size: 17px;
  font-weight: 700;
  color: #1E1E2F;
  margin-bottom: 8px;
  line-height: 1.3;
}

.qa-tour-body {
  font-size: 12px;
  color: #5A5A72;
  line-height: 1.65;
  margin-bottom: 18px;
}

.qa-tour-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qa-tour-skip {
  background: none;
  border: none;
  font-size: 11px;
  color: #9A96A8;
  cursor: pointer;
  padding: 0;
}

.qa-tour-skip:hover {
  color: #6366F1;
}

.qa-tour-nav {
  display: flex;
  gap: 8px;
}

.qa-tour-btn {
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.qa-tour-btn-primary {
  background: #6366F1;
  color: #fff;
  border: none;
}

.qa-tour-btn-primary:hover {
  background: #4F46E5;
}

.qa-tour-btn-outline {
  background: #F5F4FA;
  color: #1E1E2F;
  border: 1.5px solid #EEECF5;
}

.qa-tour-btn-outline:hover {
  border-color: #6366F1;
  color: #6366F1;
}

/* Responsive */
@media (max-width: 960px) {
  .qa-trigger-btn {
    display: none !important;
  }
  .qa-dropdown {
    right: -60px;
    width: 340px;
  }
  .qa-panel-card {
    width: 340px;
  }
}
</style>
